/**
 * Hook for Acquired Book Office Mapping
 *
 * Fetches the mapping of offices to their associated acquired book template IDs.
 * This allows the AcquiredBooksPage to display office-level tabs with only
 * the acquired templates that have data for that specific office.
 */

import { useState, useCallback, useEffect } from 'react';
import { httpsCallable } from 'firebase/functions';
import { functions } from '../lib/firebase';

export interface OfficeWithAcquiredBooks {
  officeId: string;
  officeName: string;
  acquiredTemplateIds: string[];
}

export interface UseAcquiredBookOfficeMappingReturn {
  offices: OfficeWithAcquiredBooks[];
  loading: boolean;
  error: Error | null;
  refresh: () => Promise<void>;
}

/**
 * Hook to fetch office-to-acquired-template mapping
 *
 * @param companyId - The company ID to fetch mapping for
 * @returns Object containing offices array, loading state, error, and refresh function
 */
export function useAcquiredBookOfficeMapping(companyId: string): UseAcquiredBookOfficeMappingReturn {
  const [offices, setOffices] = useState<OfficeWithAcquiredBooks[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);

  const fetchMapping = useCallback(async () => {
    if (!companyId) {
      setOffices([]);
      setError(null);
      setLoading(false);
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const getAcquiredBookOfficeMappingFn = httpsCallable<
        { companyId: string },
        { success: boolean; offices: OfficeWithAcquiredBooks[] }
      >(functions, 'getAcquiredBookOfficeMapping');

      const result = await getAcquiredBookOfficeMappingFn({ companyId });

      if (result.data.success) {
        setOffices(result.data.offices);
      } else {
        throw new Error('Failed to fetch acquired book office mapping');
      }
    } catch (err) {
      const error = err instanceof Error ? err : new Error('Unknown error fetching office mapping');
      setError(error);
      console.error('[useAcquiredBookOfficeMapping] Error:', error);
    } finally {
      setLoading(false);
    }
  }, [companyId]);

  // Fetch on mount and when companyId changes
  useEffect(() => {
    fetchMapping();
  }, [fetchMapping]);

  return {
    offices,
    loading,
    error,
    refresh: fetchMapping,
  };
}
